// Product option interfaces
export interface ProductOptionValue {
  label: string;
  value: string;
  priceAdjustment: number;
  discountRate?: number;
}

export interface ProductOption {
  id?: string;
  name: string;
  type: 'dosage' | 'quantity' | 'select' | 'radio';
  required?: boolean;
  values: ProductOptionValue[];
}

// Import SupabaseProduct as the single source of truth
export type { SupabaseProduct as Product } from '../hooks/useSupabaseProducts';

// ProductPrice interface (quantity-tier pricing attached to a variant)
export interface ProductPrice {
  id: number;
  variant_id: string;
  quantity: number;
  original_price: number;
  sale_price: number;
  discount_rate: number;
  created_at: string;
  updated_at: string;
}

// Export ProductVariant from the hook for convenience
export type { ProductVariant } from '../hooks/useSupabaseProducts';

// Helper functions for category system
export const getProductCategory = (product: any): string => {
  return product.category_name || product.category || '';
};

// Convert a category slug (e.g. "weight-management") to its display label
// (e.g. "Weight Management"). Honors overrides for slugs that don't format
// cleanly via Title-Case-on-hyphens.
const CATEGORY_LABEL_OVERRIDES: Record<string, string> = {
  'beauty-skincare': 'Beauty & Skincare',
};
export const formatCategoryLabel = (slug: string): string => {
  if (!slug || slug === 'All') return slug;
  if (CATEGORY_LABEL_OVERRIDES[slug]) return CATEGORY_LABEL_OVERRIDES[slug];
  return slug.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
};

export const getProductCategoryId = (product: any): number | null => {
  return product.category_id || null;
};

export const getProductPrice = (product: any): number => {
  return product.final_price || product.price || 0;
};

// Note: Original price and discount should be calculated from product_prices table
// These functions are deprecated and should not be used for price calculations
export const getProductOriginalPrice = (product: any): number => {
  // getProductOriginalPrice is deprecated. Use product_prices table instead.
  return 0;
};

export const getProductDiscount = (product: any): number => {
  // getProductDiscount is deprecated. Use product_prices table instead.
  return 0;
};

// User interfaces
export interface User {
  id: string;
  email: string;
  name: string;
  phone?: string;
  address?: string;
  createdAt: string;
}

// Order interfaces
export interface OrderItem {
  productId: string;
  productName: string;
  quantity: number;
  price: number;
  pillQuantity?: number;
  image?: string;
  manufacturer?: string;
}

export interface Order {
  id: string;
  userId: string;
  userName: string;
  userEmail: string;
  userPhone?: string;
  orderNumber: string;
  items: OrderItem[];
  subtotal?: number;
  totalAmount: number;
  shippingFee?: number;
  discountAmount?: number;
  status: 'payment_pending' | 'payment_confirmed' | 'shipping' | 'delivered' | 'cancelled' | 'refunded' | 'trash';
  paymentStatus: 'pending' | 'paid' | 'failed' | 'refunded';
  paymentMethod?: string;
  trackingNumber?: string;
  shippingCompany?: string;
  deliveredAt?: string;
  shippingAddress: string | Record<string, any>;
  createdAt: string;
  updatedAt: string;
}

// Crypto payment types
export type CryptoCoin = 'BTC' | 'USDT' | 'LTC' | 'XMR';
export type CryptoProvider = 'nowpayments';
export type CryptoPaymentStatus = 'pending' | 'partially_paid' | 'paid' | 'confirmed' | 'expired' | 'invalid';

export interface CryptoPaymentInfo {
  paymentId: string;
  address: string;
  amountCrypto: string;
  amountFiat: number;
  coin: CryptoCoin;
  network: string;
  expiresAt: string;
  qrData: string;
  providerInvoiceId: string;
  status: CryptoPaymentStatus;
  confirmations: number;
  requiredConfirmations: number;
}

export interface CoinOption {
  id: CryptoCoin;
  name: string;
  symbol: string;
  network: string;
  provider: CryptoProvider;
  color: string;
  // Typical range on-chain confirmation takes for this coin's defaults (minutes).
  confirmationTimeMin: { min: number; max: number };
  // Maps a transaction hash to a canonical block explorer URL.
  explorerTxUrl: (hash: string) => string;
  // Optional extra caveat about this coin's wallet ecosystem (e.g. "some exchanges
  // block XMR withdrawals"). Rendered as an info banner on the payment page.
  walletWarning?: string;
}

// Review interface - maintains legacy code compatibility + supports actual DB fields
export interface Review {
  // Basic fields
  id: string;
  rating: number;
  title?: string | null;
  content: string;
  
  // DB fields (actual Supabase schema)
  user_id?: string | null;
  product_id?: string;
  order_id?: string | null;
  images?: string[];
  is_verified?: boolean;
  created_at?: string;
  updated_at?: string;
  
  // Legacy fields (backward compatibility)
  productId?: string;
  productName?: string;
  userId?: string;
  userName?: string;
  userEmail?: string;
  status?: 'pending' | 'approved' | 'rejected';
  adminReply?: string;
  reviewedBy?: string;
  reviewedAt?: string;
  createdAt?: string;
  updatedAt?: string;
  initialViews?: number; // Initial view count (randomly generated)
}

// Notice interface
export interface Notice {
  id: string;
  title: string;
  content: string;
  author: string;
  isImportant: boolean;
  views: number;
  createdAt: string;
  updatedAt: string;
}

// Point Transaction interface
export interface PointTransaction {
  id: string;
  user_id: string;
  order_id?: string;
  review_id?: string;
  amount: number;
  type: 'earn' | 'use';
  reason: string;
  balance_after: number;
  created_at: string;
}

// Coupon interface
export interface Coupon {
  id: string;
  code: string;
  name: string;
  discount_type: 'percentage' | 'fixed';
  discount_value: number;
  min_amount: number;
  max_discount?: number;
  usage_limit?: number;
  usage_per_user: number;
  usage_count?: number;
  start_date?: string;
  end_date?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

// Coupon Usage interface
export interface CouponUsage {
  id: string;
  user_id: string;
  coupon_id: string;
  order_id: string;
  discount_amount: number;
  used_at: string;
}

// Inquiry interface
export interface Inquiry {
  id: string;
  userId: string;
  userEmail: string;
  userName: string;
  category: 'product' | 'delivery' | 'payment' | 'return' | 'general';
  title: string;
  content: string;
  orderNumber?: string;
  status: 'pending' | 'answered' | 'closed';
  adminReply?: string;
  answeredBy?: string;
  answeredAt?: string;
  createdAt: string;
  updatedAt: string;
}

// Health Article interface
export interface HealthArticle {
  id: string | number;
  title: string;
  content?: string;
  author: string;
  category?: string;
  tags?: string[];
  summary: string;
  featuredImage?: string;
  thumbnail?: string; // Dummy data compatibility
  date?: string; // Dummy data compatibility
  isPublished?: boolean;
  publishedAt?: string;
  views: number;
  createdAt?: string;
  updatedAt?: string;

  // Optional fields for enhanced functionality
  slug?: string;
  metaDescription?: string;
  readTimeMinutes?: number;
  featured?: boolean;
}

// Category interface
export interface Category {
  id: number;
  name: string;
  parentId?: number;
  level: number;
  displayOrder?: number;
  children?: Category[];
}

// Admin User interface
export interface AdminUser {
  id: string;
  email: string;
  name: string;
  role: 'admin' | 'manager';
  lastLogin?: string;
}